<template>
  <div v-if="showAdminWarning" class="p-16 bg-bg-warning text-text-warning type-sm">
    This component is not available on corporate domains
  </div>
  <div
    v-else-if="!isNonEcomSite"
    ref="parallaxContainerRef"
    v-editable="blok"
    class="SwatchSwitcherBlock layout-container relative"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div class="grid grid-cols-1 desk:grid-cols-2 gap-16">
      <div
        class="grid grid-cols-6 gap-16 order-last desk:order-first"
        :class="{
          'desk:order-last': blok.reverseLayout,
        }"
      >
        <div class="col-start-1 desk:col-start-2 -col-end-1 desk:-col-end-2 flex flex-col justify-center gap-32 desk:gap-48">
          <div class="flex flex-col gap-8 order-last desk:order-first">
            <div class="type-tagline-sm"> {{ blok.tagline }}</div>
            <div class="wysiwyg">
              <ExtendedWysiwyg :content="blok.content" />
            </div>
            <div v-if="blok.buttonText" class="flex flex-wrap gap-20 desk:gap-24 mt-8 desk:mt-12">
              <nuxt-link
                v-if="blok.buttonText"
                :to="$sbLinkFix(blok.buttonLink)"
                :target="$sbLinkTarget(blok.buttonLink)"
              >
                <div
                  class="btn"
                  :class="{
                    'btn--secondary' : blok.buttonStyle === 'secondary',
                    'btn--underline' : blok.buttonStyle === 'underline',
                  }"
                >
                  {{ blok.buttonText }}
                </div>
              </nuxt-link>
            </div>
          </div>
          <ul class="flex flex-row gap-12 desk:gap-16">
            <li
              v-for="(item, index) in blok.listItem"
              :key="item._uid"
              class="flex flex-col items-center gap-8"
              @mouseenter="handleHover(item, index)"
            >
              <div
                class="aspect-square w-64 desk:w-96 h-64 desk:h-96 transition duration-300 ease-in-out"
                :class="{
                  'opacity-100 outline outline-2 outline-offset-1': activeIndex === index,
                  'opacity-50 outline-none': activeIndex !== index,
                }"
              >
                <nuxt-img
                  v-if="item.swatchImage && item.swatchImage.filename"
                  preset="standard"
                  sizes="mob375:64px mob390:64px mob414:64px desk:96px"
                  class="h-full w-full object-cover"
                  :src="item.swatchImage.filename"
                  :alt="item.swatchImage.alt"
                />
              </div>
              <div
                class="type-xs text-center"
                :class="{
                  'text-text-base': activeIndex === index,
                  'text-text-inactive': activeIndex !== index
                }"
              >
                {{ item.name }}
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div class="relative w-full grid grid-cols-6 gap-16">
        <div
          class="w-full aspect-square row-start-1 row-end-1 overflow-hidden"
          :class="{
            'col-start-1 -col-end-2': hoverItemSecond && !blok.reverseLayout,
            'col-start-2 -col-end-1': hoverItemSecond && blok.reverseLayout,
            'col-start-1 -col-end-1': !hoverItemSecond,
          }"
        >
          <nuxt-img
            :style="blok.parallaxEffect ? { transform: `translateY(${reverseOffset}px) scale(1.2)` } : {}"
            preset="standard"
            loading="lazy"
            sizes="mob375:100vw mob390:100vw mob414:100vw desk:1260px"
            :src="hoverItem"
            class="w-full h-full object-cover transition-transform duration-[50ms]"
            :class="focalPointClass"
            :alt="hoverItemAlt"
          />
        </div>
        <div
          v-if="hoverItemSecond"
          class="relative w-full aspect-square mt-64 row-start-1 row-end-1"
          :class="{
            '-col-end-1 col-span-2': !blok.reverseLayout,
            'col-start-1 col-span-2': blok.reverseLayout,
          }"
        >
          <nuxt-img
            :style="blok.parallaxEffect ? { transform: `translateY(${offset}px)` } : {}"
            preset="standard"
            loading="lazy"
            sizes="mob375:100vw mob390:100vw mob414:100vw desk:630px"
            :src="hoverItemSecond"
            class="absolute inset-0 w-full h-full object-cover transition-transform duration-[50ms]"
            :class="secondFocalPointClass"
            :alt="hoverItemSecondAlt"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  DatasourceObjectPosition,
  StoryBlokAsset, StoryBlokLink
} from '#constants/types/storyblok';

import useVisualAppearancePlugin from '~/composables/useVisualAppearancePlugin';
import { ref, computed } from 'vue';
import type { ISbRichtext } from 'storyblok-js-client';
import { useElementBounding } from '@vueuse/core';

const { isNonEcomSite, showAdminWarning } = useNonEcomGuard();

interface Item {
  name: string;
  swatchImage: StoryBlokAsset;
  highlightImage: StoryBlokAsset;
  focalPoint: DatasourceObjectPosition;
  additionalImage: StoryBlokAsset;
  additionalFocalPoint: DatasourceObjectPosition;
}

interface Props {
  tagline: string;
  content: ISbRichtext;
  buttonText: string;
  buttonStyle: 'primary' | 'secondary' | 'underline';
  buttonLink: StoryBlokLink;
  listItem: Item[];
  reverseLayout: boolean;
  parallaxEffect: boolean;
}

const props = defineProps<{
  blok: Props,
}>();

const hoverItem = ref(props.blok.listItem[0]?.highlightImage?.filename);
const hoverItemSecond = ref(props.blok.listItem[0]?.additionalImage?.filename || props.blok.listItem[0]?.additionalImage?.filename);
const activeIndex = ref(0);

const hoverItemAlt = computed(() => props.blok.listItem[activeIndex.value]?.highlightImage?.alt || '');
const hoverItemSecondAlt = computed(() => props.blok.listItem[activeIndex.value]?.additionalImage?.alt || '');

const handleHover = (item: Item, index: number) => {
  hoverItem.value = item.highlightImage.filename;
  hoverItemSecond.value = item.additionalImage?.filename || null;
  activeIndex.value = index;
};

const focalPointClass = computed(() => {
  const focalPoint = props.blok.listItem[activeIndex.value]?.focalPoint;
  return focalPoint ? `${focalPoint}` : '';
});

const secondFocalPointClass = computed(() => {
  const secondFocalPoint = props.blok.listItem[activeIndex.value]?.additionalFocalPoint;
  return secondFocalPoint ? `${secondFocalPoint}` : '';
});

const parallaxContainerRef = useTemplateRef('parallaxContainerRef');
const { top } = useElementBounding(parallaxContainerRef);

const windowHeight = ref(0);
onMounted(()=> {
  windowHeight.value = window.innerHeight;
});

const progress = computed(() => {
  const start = windowHeight.value;
  const end = -400;
  return Math.max(0, Math.min((start - top.value) / (start - end), 1));
});

const offset = computed(() => progress.value * 40);
const reverseOffset = computed(() => -offset.value);

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);
</script>

<style scoped lang="postcss">
.outline-offset-1 {
  outline-offset: 1px;
  outline-color: #6B6865;
}
:deep(.wysiwyg p) {
  @apply text-16 desk:text-18 font-open normal-case text-pretty text-text-subtle leading-24 desk:leading-26;
}
</style>
